import { create } from 'zustand'
import type {
  VoiceSessionState,
  VoiceInfo,
  VoiceProviderType,
} from '@/connectors/voice'

interface VoiceState {
  // Provider state
  isInitialized: boolean
  provider: VoiceProviderType | null
  voices: VoiceInfo[]

  // Session state
  sessionState: VoiceSessionState
  isListening: boolean
  isSpeaking: boolean

  // Audio settings
  inputDeviceId: string | null
  outputDeviceId: string | null
  inputVolume: number
  outputVolume: number
  vadEnabled: boolean
  vadThreshold: number

  // Transcription
  currentTranscript: string
  finalTranscript: string
  agentMessage: string

  // Actions
  setInitialized: (initialized: boolean, provider: VoiceProviderType) => void
  setVoices: (voices: VoiceInfo[]) => void
  setSessionState: (state: VoiceSessionState) => void
  setListening: (listening: boolean) => void
  setSpeaking: (speaking: boolean) => void
  setInputDevice: (deviceId: string | null) => void
  setOutputDevice: (deviceId: string | null) => void
  setInputVolume: (volume: number) => void
  setOutputVolume: (volume: number) => void
  setVadEnabled: (enabled: boolean) => void
  setVadThreshold: (threshold: number) => void
  setCurrentTranscript: (transcript: string) => void
  setFinalTranscript: (transcript: string) => void
  setAgentMessage: (message: string) => void
  reset: () => void
}

const initialState = {
  isInitialized: false,
  provider: null as VoiceProviderType | null,
  voices: [] as VoiceInfo[],
  sessionState: 'idle' as VoiceSessionState,
  isListening: false,
  isSpeaking: false,
  inputDeviceId: null as string | null,
  outputDeviceId: null as string | null,
  inputVolume: 1,
  outputVolume: 1,
  vadEnabled: true,
  vadThreshold: 0.01,
  currentTranscript: '',
  finalTranscript: '',
  agentMessage: '',
}

export const useVoiceStore = create<VoiceState>()((set) => ({
  ...initialState,

  setInitialized: (initialized, provider) =>
    set({ isInitialized: initialized, provider }),

  setVoices: (voices) => set({ voices }),

  setSessionState: (state) =>
    set({
      sessionState: state,
      isListening: state === 'listening',
      isSpeaking: state === 'speaking',
    }),

  setListening: (listening) => set({ isListening: listening }),

  setSpeaking: (speaking) => set({ isSpeaking: speaking }),

  setInputDevice: (deviceId) => set({ inputDeviceId: deviceId }),

  setOutputDevice: (deviceId) => set({ outputDeviceId: deviceId }),

  setInputVolume: (volume) => set({ inputVolume: volume }),

  setOutputVolume: (volume) => set({ outputVolume: volume }),

  setVadEnabled: (enabled) => set({ vadEnabled: enabled }),

  setVadThreshold: (threshold) => set({ vadThreshold: threshold }),

  setCurrentTranscript: (transcript) => set({ currentTranscript: transcript }),

  setFinalTranscript: (transcript) =>
    set({ finalTranscript: transcript, currentTranscript: '' }),

  setAgentMessage: (message) => set({ agentMessage: message }),

  reset: () => set(initialState),
}))
